import Immutable from 'immutable';
import { Markup, MarkupList, RawMarkup } from 'records/Markup';
import { MarkupResponse, RawMarkupResponse } from 'records/MarkupResponse';
import { RawSheetVersion } from 'records/SheetVersion';
import GQLRequest from 'utils/GQLRequest';

export const getNextMarkups = async (
  cursor: string
): Promise<MarkupResponse> => {
  const { nextMarkups } = await new GQLRequest()
    .query(
      `
      query NextMarkups($cursor: String!) {
        nextMarkups(cursor: $cursor) {
          markups {
            markupid
            fileid
            projectid
            jsonBlob
          }
          cursor
        }
      }
      `
    )
    .variables({ cursor })
    .json<{ nextMarkups: RawMarkupResponse }>();

  return MarkupResponse.deserialize(nextMarkups);
};

export const getMarkups = async (
  fileid: string,
  pageSize = 100
): Promise<MarkupList> => {
  const { markups } = await new GQLRequest()
    .query(
      `
      query Markups($fileid: String!, $pageSize: Int!) {
        markups(fileid: $fileid, pageSize: $pageSize) {
          markups {
            markupid
            fileid
            projectid
            jsonBlob
          }
          cursor
        }
      }
      `
    )
    .variables({ fileid, pageSize })
    .json<{ markups: RawMarkupResponse }>();

  let markupsList = Immutable.List(markups.markups.map(Markup.deserialize));
  let { cursor } = markups;

  while (cursor) {
    const { markups: nextMarkups, cursor: nextCursor } = await getNextMarkups(
      cursor
    );

    markupsList = markupsList.concat(nextMarkups);
    cursor = nextCursor;
  }

  return markupsList;
};

export const getMarkup = async (
  id: string,
  fileid: string
): Promise<Markup> => {
  const { markup } = await new GQLRequest()
    .query(
      `
    query Markup($id: String!, $fileid: String!) {
      markup(id: $id, fileid: $fileid) {
        markupid
        fileid
        projectid
        jsonBlob
      }
    }
    `
    )
    .variables({ id, fileid })
    .json<{ markup: RawMarkup }>();

  return Markup.deserialize(markup);
};

export const createMarkup = async (markup: Markup): Promise<Markup> => {
  const { fileid, projectid, jsonBlob, markupid } = markup;

  const resp = await new GQLRequest()
    .query(
      `
      mutation CreateMarkup($fileid: String!, $projectid: String!, $jsonBlob: String!, $clientGeneratedID: String!) {
        createMarkup(fileid: $fileid, projectid: $projectid, jsonBlob: $jsonBlob, clientGeneratedID: $clientGeneratedID) {
          markupid
          fileid
          projectid
          jsonBlob
        }
      }
      `
    )
    .variables({ fileid, projectid, jsonBlob, clientGeneratedID: markupid })
    .json<{ createMarkup: RawMarkup }>();

  return Markup.deserialize(resp.createMarkup);
};

export const updateMarkup = async (updatedMarkup: Markup): Promise<Markup> => {
  const resp = await new GQLRequest()
    .query(
      `
      mutation UpdateMarkup($id: String!, $fileid: String!, $jsonBlob: String!) {
        updateMarkup(id: $id, fileid: $fileid, jsonBlob: $jsonBlob) {
          markupid
          fileid
          projectid
          jsonBlob
        }
      }
      `
    )
    .variables({
      id: updatedMarkup.markupid,
      fileid: updatedMarkup.fileid,
      jsonBlob: updatedMarkup.jsonBlob,
    })
    .json<{ updateMarkup: RawMarkup }>();

  return Markup.deserialize(resp.updateMarkup);
};

export const deleteMarkup = async (markupToDelete: Markup): Promise<string> => {
  const resp = await new GQLRequest()
    .query(
      `
      mutation DeleteMarkup($id: String!, $fileid: String!) {
        deleteMarkup(id: $id, fileid: $fileid) 
      }
      `
    )
    .variables({ id: markupToDelete.markupid, fileid: markupToDelete.fileid })
    .json<{ deleteMarkup: string }>();

  return resp.deleteMarkup;
};

export const getParentSheet = async (
  markupid: string,
  projectid: string
): Promise<RawSheetVersion> => {
  const resp = await new GQLRequest()
    .query(
      `
      query MarkupToSheet($markupid: String!, $projectid: String!) {
        markupToSheet(markupid: $markupid, projectid: $projectid) {
          sheetid
          sheetnumber
          sheettitle
          discipline
          issueddate
          revisioncomment
          publishedtimestamp
          publishedBy
          pageid
          documentid
          projectid
          filename
          documentTitle
          pageLabel
          pageIndex
          width
          height
          uploadedBy
          uploadtimestamp
          processingRequestid
        }
      }
    `
    )
    .variables({ markupid, projectid })
    .json<{ markupToSheet: RawSheetVersion }>();

  return resp.markupToSheet;
};
